/**
 * 全局错误边界（2026-10-22 新增）。
 *
 * 需求来源（线上事故）：用户在设计项目详情上传文件，连续三次上传后整页白屏 ——
 * 页面只剩左侧导航图标和一块黑卡，业务内容全没了，也没有任何错误提示。
 *
 * 为什么白屏：React 18 中，渲染期抛出的异常如果没有 ErrorBoundary 接住，
 * 会一路冒泡到根，React 直接卸载整棵树（实测 `#root` 子节点变成 0）。
 * 本次事故的直接原因是提示卡渲染了对象（后端结构化 detail 被直接塞进 toast），
 * 但同类问题不止这一处：任何一个组件在渲染期抛错，用户看到的都是同一片白。
 * 比崩溃更糟的是「崩溃了却什么都不说」—— 用户只能反复重试。
 *
 * 这一层的作用不是掩盖 Bug，而是把「整站消失」降级成「一块区域报错 + 可恢复」：
 *   · 局部边界（页面级 / 详情抽屉级）出错只影响该区域，导航与其它页面照常可用；
 *   · 明确告诉用户「出错了」并给出错误摘要，便于用户反馈、便于排查；
 *   · 提供「重试」按钮，清掉错误状态重新挂载子树，多数偶发渲染错误可自行恢复。
 *
 * 关键取舍：
 *   · 不做自动上报：项目没有前端错误采集服务，凭空发请求只会制造新的失败点；
 *     错误摘要留在界面上，由用户按需反馈。
 *   · 开发环境仍打印完整堆栈（componentDidCatch），生产环境保留控制台输出，
 *     不影响现有排障习惯。
 */
import React from "react";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // 保留控制台堆栈：白屏问题只能靠这里定位，删掉就等于把排障线索也删了。
    console.error("[ErrorBoundary] 渲染异常：", error, info?.componentStack || "");
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    // 传了自定义兜底就交给它（例如抽屉里只想占一小块）。
    if (this.props.fallback) return this.props.fallback(error, this.reset);
    return (
      <div className="flex min-h-[240px] flex-col items-center justify-center gap-3 p-6" data-testid="app-error-boundary">
        <p className="text-sm font-bold text-slate-800">这一块加载出错了</p>
        <p className="max-w-lg text-center text-xs text-slate-500">
          页面其它部分仍可正常使用。可以点「重试」重新加载这一块；若反复出现，请把下面的信息反馈给技术同事。
        </p>
        <p className="max-w-lg break-all rounded-md bg-slate-100 px-2 py-1 text-center text-[11px] text-slate-600">
          {String(error?.message || error)}
        </p>
        <button
          type="button"
          onClick={this.reset}
          data-testid="app-error-boundary-retry"
          className="rounded-md bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700"
        >
          重试
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
